import { call, put } from 'redux-saga/effects';
import apiUrl from 'common/api-url';
import * as api from 'reduxStore/apiService';
import { trackMenuGroupSaved } from 'components/menu/tracking/menuSegmentTracking';

import { MenuGroupResponse } from './types';

import {
  archiveMenuGroup,
  getMenuGroup,
  getMenuGroupSuccess,
  getMenuGroupFailure,
  setMenuGroup,
  setMenuGroupSuccess,
  setMenuGroupFailure,
  deleteMenuGroup,
  deleteMenuGroupSuccess,
  deleteMenuGroupFailure,
} from './actions';

export function* getMenuGroupRequested(action: ReturnType<typeof getMenuGroup>) {
  try {
    const { id } = action.payload;
    const payload: MenuGroupResponse = yield call(api.get, apiUrl('MENU_GROUP', { id }));

    yield put(getMenuGroupSuccess(payload));
  } catch (err) {
    yield put(getMenuGroupFailure());
  }
}

export function* setMenuGroupRequested(action: ReturnType<typeof setMenuGroup>) {
  try {
    const { id } = action.payload;
    const isNew = !id;
    const shouldArchive = !action.payload.active;
    const url = apiUrl(isNew ? 'MENU_GROUP_CREATE' : 'MENU_GROUP', { id });
    const apiMethod = isNew ? api.post : api.put;
    const description = {
      content: action.payload?.description || '',
      contentType: 'HTML',
    };
    const response: MenuGroupResponse | undefined = yield call(
      apiMethod,
      url,
      { ...action.payload, description },
      true,
    );

    yield put(setMenuGroupSuccess());

    // Reported here rather than from the modal so a server-rejected save is
    // never counted as completed, and so a create can carry the id the server
    // just assigned (THY-2347).
    //
    // A create is checked first: the modal submits only `name` and
    // `description` for a new group, so `active` is absent and `shouldArchive`
    // reads true — which reported every create as an archive (THY-2397 QA). A
    // new group is always created active.
    const isActive = isNew || !shouldArchive;

    trackMenuGroupSaved({
      menuGroupId: id || response?.id,
      operation: isNew ? 'add_new' : shouldArchive ? 'archive' : 'edit',
      isActive,
      group: {
        name: action.payload?.name,
        description: action.payload?.description,
        active: isActive,
      },
    });

    if (shouldArchive) {
      yield put(archiveMenuGroup());
    }
  } catch (xhr: any) {
    const response = xhr.responseJson;
    yield put(
      setMenuGroupFailure({
        serverErrorName: response.errors[0].name,
      }),
    );
  }
}

export function* deleteMenuGroupRequested(action: ReturnType<typeof deleteMenuGroup>) {
  try {
    const { id } = action.payload;
    yield call(api.del, apiUrl('MENU_GROUP', { id }), true);

    yield put(deleteMenuGroupSuccess());

    // Reported after the DELETE has succeeded, like the save above: the modal
    // fires its legacy event at click time, and deleting a group that still
    // holds offers is rejected server-side (THY-2347).
    // The name comes from the snapshot the modal captured when it opened.
    trackMenuGroupSaved({ menuGroupId: id, operation: 'delete', isActive: false });
  } catch (xhr: any) {
    const response = xhr.responseJson;
    yield put(
      deleteMenuGroupFailure({
        serverErrorName: response.errors[0].name,
      }),
    );
  }
}
